import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import { type AdminProduct, type AdminProductListResponse, type AdminBulkPricingTier } from "./useAdmin";

// Products CRUD hooks
export const useProducts = (params: Record<string, any> = {}, enabled: boolean = true) => {
    return useQuery<AdminProductListResponse>({
        queryKey: ['admin', 'products', params],
        queryFn: async () => {
            const queryString = new URLSearchParams(params).toString();
            const url = `/products/${queryString ? `?${queryString}` : ''}`;
            const { data } = await axiosInstance.get(url);
            return data;
        },
        enabled,
    });
};

export const useProduct = (id: number, enabled: boolean = true) => {
    return useQuery<AdminProduct>({
        queryKey: ['admin', 'products', id],
        queryFn: async () => {
            const { data } = await axiosInstance.get(`/products/${id}/`);
            return data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminProduct, Error, Omit<AdminProduct, 'id' | 'min_price' | 'max_price' | 'created' | 'updated'>>({
        mutationFn: async (productData) => {
            const { data } = await axiosInstance.post('/products/', productData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
        },
    });
};

export const useUpdateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminProduct, Error, { id: number; data: Partial<Omit<AdminProduct, 'id' | 'min_price' | 'max_price' | 'created' | 'updated'>> }>({
        mutationFn: async ({ id, data }) => {
            const { data: response } = await axiosInstance.patch(`/products/${id}/`, data);
            return response;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'products', id] });
        },
    });
};

export const useDeleteProduct = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`/products/${id}/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
        },
    });
};

// Bulk Pricing CRUD hooks
export const useProductBulkPricing = (productId: number, enabled: boolean = true) => {
    return useQuery<AdminBulkPricingTier[]>({
        queryKey: ['admin', 'products', productId, 'bulk-pricing'],
        queryFn: async () => {
            const { data } = await axiosInstance.get(`/products/${productId}/bulk-pricing/`);
            return data.results || data;
        },
        enabled: enabled && !!productId,
    });
};

export const useCreateBulkPricing = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminBulkPricingTier, Error, { productId: number; data: Omit<AdminBulkPricingTier, 'id'> }>({
        mutationFn: async ({ productId, data }) => {
            const { data: response } = await axiosInstance.post(`/products/${productId}/bulk-pricing/`, data);
            return response;
        },
        onSuccess: (_, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'products', productId, 'bulk-pricing'] });
        },
    });
};

export const useUpdateBulkPricing = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminBulkPricingTier, Error, { productId: number; id: number; data: Partial<Omit<AdminBulkPricingTier, 'id'>> }>({
        mutationFn: async ({ productId, id, data }) => {
            const { data: response } = await axiosInstance.patch(`/products/${productId}/bulk-pricing/${id}/`, data);
            return response;
        },
        onSuccess: (_, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'products', productId, 'bulk-pricing'] });
        },
    });
};

export const useDeleteBulkPricing = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { productId: number; id: number }>({
        mutationFn: async ({ productId, id }) => {
            await axiosInstance.delete(`/products/${productId}/bulk-pricing/${id}/`);
        },
        onSuccess: (_, { productId }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'products', productId, 'bulk-pricing'] });
        },
    });
};
